import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as tokens from "@amzn/awsui-design-tokens-core";
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  CollectionPreferences,
  CollectionPreferencesProps,
  Header,
  Link,
  Pagination,
  PropertyFilter,
  PropertyFilterProps,
  Popover,
  SegmentedControl,
  SpaceBetween,
  StatusIndicator,
  Table,
  TableProps,
} from "@amzn/awsui-components-react";
import {
  InvestigationSuggestion,
  Stage,
  UninvestigatedFilterOptions,
  UninvestigatedGroup,
} from "../../lusca-search-api/generated-src";
import {
  DEFAULT_GROUPS_QUERY,
  GroupsQuery,
  GroupsSortField,
  useInvestigationSuggestions,
  useUninvestigatedGroups,
} from "../../hooks/useInvestigations";
import type { SuggestionStart } from "./InvestigationSuggestions";
import { GroupsFilterQuery, serializeGroupsFilter } from "./groupsFilter";
import { FUNNEL_LABELS, browseLabel } from "./InvestigationFunnel";
import { formatWhen } from "./formatWhen";
import {
  HIGH_IMPACT_USD,
  NUMERIC_FILTER_OPERATORS,
  RowSeed,
  formatSnapshotTimestamp,
  formatTimestamp,
  propertyFilterI18n,
  sectionPath,
  UNCATEGORIZED,
} from "./investigationUtils";
import { formatGrowth } from "./trendUtils";
import { shortPatternId, suggestionHeadline } from "./trackstarsText";

/**
 * The "not yet investigated" side of the funnel: every item in the latest snapshot that no
 * investigation covers, pre-grouped by queue, error type and templated message so a whole
 * population can be picked up at once. (The API still calls these "uninvestigated groups".)
 *
 * The table asks for the category view: a category is a human saying "these groups are the same
 * issue", so groups that share one collapse into a single row (items and impact summed) that
 * expands into its (queue, error type) split. Uncategorized groups are not the same issue and
 * stay one row each. Starting an investigation from a category row seeds a category-only rule;
 * from a member, the rule also pins the queue and error type.
 *
 * Gamma reconciles thousands of groups, so — like the population Items tab — paging, filtering
 * and sorting happen server-side and the browser only ever holds the current page.
 */

export const BACKLOG_TITLE = FUNNEL_LABELS.backlog;
export const START_LABEL = "Start investigation";

/**
 * The All | Uncategorized | Categorized switch beside the filter bar. Its token
 * (`Category = uncategorized` / `Category != uncategorized`) is part of the query sent to the
 * server but not shown as a token in the bar: the switch already says it.
 */
type CategorySlice = "all" | "uncategorized" | "categorized";
const isSliceToken = (t: PropertyFilterProps.Token): boolean =>
  t.propertyKey === "category" &&
  t.value === "uncategorized" &&
  (t.operator === "=" || t.operator === "!=");
export const categorySliceOf = (
  query: PropertyFilterProps.Query
): CategorySlice => {
  if (query.operation !== "and") return "all";
  const category = query.tokens.filter((t) => t.propertyKey === "category");
  if (category.length !== 1 || !isSliceToken(category[0])) return "all";
  return category[0].operator === "=" ? "uncategorized" : "categorized";
};
/** The header action when the selected row is a TrackStars pattern. */
export const START_FROM_TRACKSTARS_LABEL = "Start from TrackStars";
/** How many groups the work-queue preview shows. */
export const PREVIEW_SIZE = 5;
const DEFAULT_PAGE_SIZE = 25;
/** Matches the API's limit cap. */
const MAX_PAGE_SIZE = 200;

const usd = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/** "$12,345" for a summed impact; "—" when the reconciliation predates the impact column. */
export const formatImpact = (value?: number | null): string =>
  value === undefined || value === null ? "—" : usd.format(value);

/** Projection of a backlog row (a group, a collapsed category or one of its members) for the table. */
export interface BacklogRow {
  key: string;
  /** Empty on a category row spanning several queues. */
  queue: string;
  /** Empty on a category row spanning several error types. */
  errorType: string;
  /** Empty when the row's groups carry several message variants. */
  errorMessage: string;
  category: string;
  items: number;
  impactUsd: number | null;
  /** Item change over the last 7 days of snapshots; null with a single snapshot. */
  growth: number | null;
  /** Newest dlq_timestamp in the group (snapshot text, UTC); null before the field existed. */
  lastSeen: string | null;
  /** Category rows: the (queue, error type) split, highest impact first. */
  members?: BacklogRow[];
  /** Distinct queues / error types / templated messages among the row's groups. */
  queues: number;
  errorTypes: number;
  messageVariants: number;
  group: UninvestigatedGroup;
}

const distinct = (values: string[]): number => new Set(values).size;

/** How many distinct queues / error types a row spans: its members' when it has any, else its own. */
const span = (
  members: BacklogRow[],
  pick: (m: BacklogRow) => string,
  own: string | undefined
): number => {
  if (members.length) return distinct(members.map(pick));
  return own ? 1 : 0;
};

const toRow = (g: UninvestigatedGroup, parentKey = ""): BacklogRow => {
  const category = g.errorCategory || UNCATEGORIZED;
  const members = (g.members ?? []).map((m) => toRow(m, `cat:${category}`));
  // Reconciliation groups by all four rule fields; category is part of the identity, and a
  // colliding trackBy makes the table render the same row over and over. A category row is
  // identified by its category; a member by category, queue and error type.
  const key = members.length
    ? `cat:${category}`
    : [parentKey, g.eventsourcekey, g.errorType, g.errorMessage, category]
        .map((v) => v ?? "")
        .join("|");
  return {
    key,
    queue: g.eventsourcekey ?? "",
    errorType: g.errorType ?? "",
    errorMessage: g.errorMessage ?? "",
    category,
    items: g.count ?? 0,
    impactUsd:
      g.impactUsd === undefined || g.impactUsd === null ? null : g.impactUsd,
    growth: g.growth7d ?? null,
    lastSeen: g.latestDlqTimestamp || null,
    members: members.length ? members : undefined,
    queues: span(members, (m) => m.queue, g.eventsourcekey ?? undefined),
    errorTypes: span(members, (m) => m.errorType, g.errorType ?? undefined),
    messageVariants: g.messageVariants ?? (g.errorMessage ? 1 : 0),
    group: g,
  };
};

export function toBacklogRows(groups: UninvestigatedGroup[]): BacklogRow[] {
  return groups.map((g) => toRow(g));
}

const isCategoryRow = (row: BacklogRow): boolean => !!row.members?.length;

const plural = (n: number, noun: string): string =>
  `${n.toLocaleString()} ${noun}${n === 1 ? "" : "s"}`;

/**
 * "24/09/2026, 10:15" for a snapshot timestamp ("2026-09-24 08:15:00.000", UTC), in the viewer's
 * locale and zone; "—" when the reconciliation predates the field.
 */
export const formatLastSeen = (value: string | null): string => {
  if (!value) return "—";
  const iso = value.includes("T") ? value : `${value.replace(" ", "T")}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime())
    ? value
    : formatTimestamp(date.toISOString());
};

/** Highest impact first (unreconciled impact ranks below $0), then most items. */
export const byImpactThenCount = (a: BacklogRow, b: BacklogRow): number =>
  (b.impactUsd ?? -1) - (a.impactUsd ?? -1) || b.items - a.items;

// A category row (or a member spanning message variants) carries null identity fields on the wire;
// the seed drops them so the population builder pins only what the row stands for.
export const seedFromGroup = (group: UninvestigatedGroup): RowSeed => ({
  dlqPk: group.sampleDlqPks?.[0] || "",
  eventsourcekey: group.eventsourcekey ?? undefined,
  error_type: group.errorType ?? undefined,
  error_message: group.errorMessage ?? undefined,
  error_category: group.errorCategory ?? undefined,
});

const nowrap: React.CSSProperties = { whiteSpace: "nowrap" };
const anywhere: React.CSSProperties = { overflowWrap: "anywhere" };
// Key order is the column order (columnDisplay follows it). Category leads:
// it is the grouping key in the default view and the one thing a reader
// scans down, so it also carries the expand toggle.
const COLUMN_LABELS: Record<string, string> = {
  category: "Category",
  queue: "Queue",
  errorType: "Error type",
  errorMessage: "Message",
  items: "Items",
  impact: "Impact",
  growth: "7-day change",
  lastSeen: "Last seen",
};

/** Table sortingField → API sort parameter. */
const SORT_FIELD_MAP: Record<string, GroupsSortField> = {
  queue: "eventsourcekey",
  errorType: "errorType",
  category: "errorCategory",
  errorMessage: "errorMessage",
  items: "count",
  impactUsd: "impactUsd",
  growth: "growth7d",
  lastSeen: "latestDlqTimestamp",
};

const DEFAULT_SORT_COLUMN: TableProps.SortingColumn<BacklogRow> = {
  sortingField: "impactUsd",
};

const GROWTH_SORT_COLUMN: TableProps.SortingColumn<BacklogRow> = {
  sortingField: "growth",
};

/**
 * A filter that keeps only growing groups (the "Growing fastest" slice) is ranked by growth;
 * anything else by impact. Applied whenever a new filter is handed in, so the user can still re-sort.
 */
const sortColumnFor = (
  filter: GroupsFilterQuery | undefined
): TableProps.SortingColumn<BacklogRow> =>
  filter?.tokens.some((t) => t.propertyKey === "growth")
    ? GROWTH_SORT_COLUMN
    : DEFAULT_SORT_COLUMN;

/**
 * PropertyFilter properties. Keys are the API's `filter` property names, so the query is sent to
 * the server as-is; the server applies it across every reconciled group, not just this page.
 */
export const BACKLOG_FILTERING_PROPERTIES: PropertyFilterProps.FilteringProperty[] =
  [
    {
      key: "queue",
      propertyLabel: COLUMN_LABELS.queue,
      groupValuesLabel: "Queues",
      operators: ["=", "!=", ":", "!:"],
    },
    {
      key: "errorType",
      propertyLabel: COLUMN_LABELS.errorType,
      groupValuesLabel: "Error types",
      operators: ["=", "!=", ":", "!:"],
    },
    {
      key: "category",
      propertyLabel: COLUMN_LABELS.category,
      groupValuesLabel: "Categories",
      operators: ["=", "!=", ":", "!:"],
    },
    {
      key: "errorMessage",
      propertyLabel: COLUMN_LABELS.errorMessage,
      groupValuesLabel: "Messages",
      operators: [":", "!:"],
    },
    {
      key: "items",
      propertyLabel: COLUMN_LABELS.items,
      groupValuesLabel: "Item counts",
      operators: NUMERIC_FILTER_OPERATORS,
    },
    {
      key: "impactUsd",
      propertyLabel: "Impact (USD)",
      groupValuesLabel: "Impact amounts",
      operators: NUMERIC_FILTER_OPERATORS,
    },
    {
      // Item change over the last 7 days of snapshots; a group seen once has none and never matches.
      key: "growth",
      propertyLabel: COLUMN_LABELS.growth,
      groupValuesLabel: "Item changes",
      operators: NUMERIC_FILTER_OPERATORS,
    },
    {
      // The API compares the value as a date/time prefix of the group's newest dlq_timestamp,
      // so "Last seen >= 2026-09-24" reads "has an item that failed on or after that day (UTC)".
      key: "lastSeen",
      propertyLabel: COLUMN_LABELS.lastSeen,
      groupValuesLabel: "Dates (YYYY-MM-DD, UTC)",
      operators: NUMERIC_FILTER_OPERATORS,
    },
  ];

/**
 * Suggestion values for the enumerable properties. These come from the server (distinct values
 * over every reconciled group), so the dropdown offers a queue even when it is not on this page.
 */
export function backlogFilteringOptions(
  options?: UninvestigatedFilterOptions
): PropertyFilterProps.FilteringOption[] {
  const map = (propertyKey: string, values?: string[]) =>
    (values ?? []).map((value) => ({ propertyKey, value }));
  return [
    ...map("queue", options?.queues),
    ...map("errorType", options?.errorTypes),
    ...map("category", options?.categories),
  ];
}

const EMPTY_FILTER: PropertyFilterProps.Query = {
  operation: "and",
  tokens: [],
};

/** A filter handed in from outside (URL, sidebar preset) as the PropertyFilter's query. */
const fromGroupsFilter = (
  filter: GroupsFilterQuery | undefined
): PropertyFilterProps.Query =>
  filter
    ? {
        operation: filter.operation,
        tokens: filter.tokens.map((t) => ({
          propertyKey: t.propertyKey,
          operator: t.operator,
          value: t.value,
        })),
      }
    : EMPTY_FILTER;

const PROPERTY_FILTER_I18N = propertyFilterI18n(
  "Filter groups",
  "Filter by queue, error type, category, items, impact…"
);

/** The PropertyFilter query in the hook's wire shape (drops the client-only token fields). */
export const toGroupsFilter = (
  query: PropertyFilterProps.Query
): GroupsFilterQuery | undefined =>
  query.tokens.length
    ? {
        operation: query.operation,
        tokens: query.tokens.map((t) => ({
          propertyKey: t.propertyKey,
          operator: t.operator,
          // Cloudscape types token values as `any`; they are strings from the filter bar.
          value: t.value as unknown,
        })),
      }
    : undefined;

/**
 * A Java exception's simple class name (java.lang.IllegalArgumentException →
 * IllegalArgumentException); other error types unchanged. The package adds nothing a reader
 * scans for and forces the class name to wrap mid-word.
 */
export const shortErrorType = (errorType: string): string =>
  /^[a-z][\w$]*(\.[a-z][\w$]*)+\.[A-Z][\w$]*$/.test(errorType)
    ? errorType.slice(errorType.lastIndexOf(".") + 1)
    : errorType;

/**
 * Text that wraps only at word boundaries: after "-", "_" and "." (queue names and snake_case
 * error types have no spaces) and inside CamelCase words, never mid-word.
 */
const BreakAtWords: React.FC<{ text: string }> = ({ text }) => {
  // Keyed by character offset: stable for a given text, unique within it.
  let offset = 0;
  return (
    <>
      {text.split(/(?<=[-_.])|(?<=[a-z])(?=[A-Z])/).map((part) => {
        const at = offset;
        offset += part.length;
        return (
          <React.Fragment key={at}>
            {at > 0 ? <wbr /> : null}
            {part}
          </React.Fragment>
        );
      })}
    </>
  );
};

/**
 * True inside the full backlog table, whose columns the user resizes: there each column has a
 * fixed width, so one-line cells fill it (ellipsis at the column edge) instead of stopping at the
 * auto-layout caps above.
 */
const FillColumn = createContext(false);
/** The stage the table shows, for cells (COLUMNS is static) that need to fetch or link. */
const BacklogStage = createContext<Stage | undefined>(undefined);
const useCap = (cap: string) => (useContext(FillColumn) ? "100%" : cap);

/** Up to three lines of a long error message; the rest on hover. */
const clamp3: React.CSSProperties = {
  display: "-webkit-box",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: 3,
  overflow: "hidden",
  overflowWrap: "anywhere",
};

/** "3 queues" on a category row whose members span several; the value itself when there is one. */
const Spanning: React.FC<{
  value: string;
  count: number;
  noun: string;
  display?: (value: string) => string;
  /** One line up to maxWidth, ellipsis beyond; the full value on hover. */
  maxWidth?: string;
}> = ({ value, count, noun, display = (v) => v, maxWidth: cap }) => {
  const fill = useContext(FillColumn);
  const maxWidth = cap && fill ? "100%" : cap;
  if (value && maxWidth)
    return (
      <span
        title={value}
        style={{
          display: "inline-block",
          maxWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          verticalAlign: "bottom",
        }}
      >
        {display(value)}
      </span>
    );
  if (value)
    return (
      <span title={value} style={{ wordBreak: "keep-all" }}>
        <BreakAtWords text={display(value)} />
      </span>
    );
  if (count <= 1) return <>—</>;
  return (
    <Box color="text-body-secondary" display="inline">
      <span style={nowrap}>{plural(count, noun)}</span>
    </Box>
  );
};

/**
 * Queue names stay on one line, so the auto-layout table gives Queue its full width before
 * Message (which wraps) gets the rest. Capped so a very long name cannot crowd out the table;
 * fits the longest current names (~55 characters).
 */
const QUEUE_MAX_WIDTH = "420px";

/**
 * Error types stay on one line too, sized after Queue. Java types are already shortened to the
 * class name; snake_case codes run to ~40 characters.
 */
const ERROR_TYPE_MAX_WIDTH = "300px";

/** Longest a category pill grows before truncating (fits ~40 characters). */
const CATEGORY_MAX_WIDTH = "300px";

// Queue name left, TrackStars mark pinned to the cell's right edge: the marks line up down the
// column instead of trailing each name at a different x.
const queueRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: tokens.spaceScaledS,
  minWidth: 0,
};

// One line: when the column narrows (e.g. a category row is expanded) the pill truncates with
// its ellipsis rather than pushing the TrackStars mark onto a second line.
const chipRow: React.CSSProperties = {
  display: "flex",
  flexWrap: "nowrap",
  alignItems: "center",
  gap: tokens.spaceScaledXxs,
  minWidth: 0,
};

/**
 * The category as a one-line badge. Cloudscape's Badge cannot shrink below its text, so a long
 * categoriser name would wrap mid-word; this pill takes the Badge's look but truncates with an
 * ellipsis to the column width, with the full name on hover (and to screen readers).
 */
const CategoryPill: React.FC<{ category: string }> = ({ category }) => {
  const red = category === UNCATEGORIZED;
  const maxWidth = useCap(CATEGORY_MAX_WIDTH);
  return (
    <span
      title={category}
      data-testid="category-pill"
      style={{
        display: "inline-block",
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        verticalAlign: "middle",
        boxSizing: "border-box",
        padding: `0 ${tokens.spaceScaledXs}`,
        borderRadius: tokens.borderRadiusBadge,
        fontSize: tokens.fontSizeBodyS,
        lineHeight: "22px",
        backgroundColor: red
          ? tokens.colorBackgroundBadgeRed
          : tokens.colorBackgroundBadgeGrey,
        color: red ? tokens.colorTextBadgeRed : tokens.colorTextBadgeGrey,
      }}
    >
      {category}
    </span>
  );
};

/**
 * An identified-but-not-yet-categorised population (R14.5): a TrackStars mark shows the bucket
 * decomposing before the categoriser update lands. An icon, not a pill, so it sits on the
 * queue's line instead of wrapping onto its own; the pattern id and run are in the popover.
 */
/** One matching pattern, told the way the Suggested by TrackStars card tells it. */
const PatternSummary: React.FC<{
  patternId: string;
  suggestion?: InvestigationSuggestion;
}> = ({ patternId, suggestion: s }) => {
  if (!s)
    return (
      <Box variant="small" color="text-body-secondary">
        {shortPatternId(patternId)}
      </Box>
    );
  const why = s.rootCause?.summary?.trim() || s.description?.trim() || "";
  const holder =
    s.ownership.state === "unowned" ? undefined : s.ownership.investigations[0];
  return (
    <SpaceBetween size="xxxs">
      <Box fontWeight="bold">{suggestionHeadline(s)}</Box>
      <Box variant="small" color="text-body-secondary">
        {shortPatternId(s.patternId)} · {s.totalMatches.toLocaleString()} items
        · {s.confidence} confidence
        {holder
          ? ` · ${
              s.ownership.state === "owned" ? "covered" : "partly covered"
            } by ${holder.owner ?? "a case"}`
          : " · nobody is on it"}
      </Box>
      {why && (
        <Box variant="small">
          {why.length > 160 ? `${why.slice(0, 157).trimEnd()}…` : why}
        </Box>
      )}
    </SpaceBetween>
  );
};

/** Popover body: the matching patterns from the latest report, each linked to its full card. */
const TrackStarsMatches: React.FC<{ stage: Stage; ids: string[] }> = ({
  stage,
  ids,
}) => {
  // Mounted only while the popover is open, so the (cached) suggestions load on demand.
  const { data, isLoading } = useInvestigationSuggestions(stage);
  const find = (id: string) =>
    data?.suggestions?.find(
      (s) =>
        s.patternId === id ||
        (s as { mergedPatternIds?: string[] }).mergedPatternIds?.includes(id)
    );
  return (
    <SpaceBetween size="s">
      {isLoading ? (
        <StatusIndicator type="loading">Loading TrackStars…</StatusIndicator>
      ) : (
        ids.map((id) => (
          <PatternSummary key={id} patternId={id} suggestion={find(id)} />
        ))
      )}
      <Box variant="small" color="text-body-secondary">
        Matched on queue, error type and message; a pattern may also pin GL or
        use case, so not every item here is necessarily part of it.
      </Box>
      <Link
        href={`${sectionPath(
          "suggestions",
          stage
        )}&pattern=${encodeURIComponent(ids.join(","))}`}
      >
        View in Suggested by TrackStars
      </Link>
    </SpaceBetween>
  );
};

const TrackStarsChips: React.FC<{ patterns?: string[] }> = ({ patterns }) => {
  const stage = useContext(BacklogStage);
  const ids = patterns ?? [];
  if (!ids.length) return null;
  // One icon per row however many patterns match; the popover shows them all.
  const label =
    ids.length === 1
      ? `Possible TrackStars match ${shortPatternId(ids[0])}`
      : `${ids.length} possible TrackStars matches`;
  return (
    <Popover
      triggerType="custom"
      dismissButton={false}
      size="large"
      header={label}
      content={
        stage ? (
          <TrackStarsMatches stage={stage} ids={ids} />
        ) : (
          <Box>{ids.map((id) => shortPatternId(id)).join(", ")}</Box>
        )
      }
    >
      <Button
        variant="inline-icon"
        iconName="gen-ai"
        ariaLabel={label}
        data-testid="trackstars-chip"
      />
    </Popover>
  );
};

/**
 * The single category the filter pins every row to (`Category = x`, ANDed with anything else),
 * or undefined. Only an Uncategorized pin hides the Category column (see hidesCategory): each
 * named category is a single row these days, so its column still carries information.
 */
export const pinnedCategory = (
  query: PropertyFilterProps.Query
): string | undefined => {
  const category = query.tokens.filter((t) => t.propertyKey === "category");
  if (category.length !== 1) return undefined;
  if (query.operation !== "and" && query.tokens.length > 1) return undefined;
  const [token] = category;
  return token.operator === "=" && typeof token.value === "string"
    ? token.value
    : undefined;
};

const COLUMNS: TableProps.ColumnDefinition<BacklogRow>[] = [
  {
    id: "category",
    header: COLUMN_LABELS.category,
    sortingField: "category",
    // Full table: user-resizable (fitColumnWidths); the pill truncates at the
    // column edge. Preview (auto layout): capped at CATEGORY_MAX_WIDTH.
    minWidth: 160,
    // A member sits under its category row; repeating the badge would only add noise.
    cell: (row) =>
      row.group.members === undefined &&
      row.group.groupCount !== undefined ? null : (
        <div style={chipRow}>
          <CategoryPill category={row.category} />
          <TrackStarsChips patterns={row.group.trackstarsPatterns} />
        </div>
      ),
  },
  {
    id: "queue",
    header: COLUMN_LABELS.queue,
    sortingField: "queue",
    // One line, ellipsis at the column edge (preview: at QUEUE_MAX_WIDTH).
    minWidth: 180,
    cell: (row) => (
      <Spanning
        value={row.queue}
        count={row.queues}
        noun="queue"
        maxWidth={QUEUE_MAX_WIDTH}
      />
    ),
  },
  {
    id: "errorType",
    header: COLUMN_LABELS.errorType,
    sortingField: "errorType",
    minWidth: 150,
    cell: (row) => (
      <Spanning
        value={row.errorType}
        count={row.errorTypes}
        noun="error type"
        display={shortErrorType}
        maxWidth={ERROR_TYPE_MAX_WIDTH}
      />
    ),
  },
  {
    id: "errorMessage",
    header: COLUMN_LABELS.errorMessage,
    sortingField: "errorMessage",
    minWidth: 200,
    cell: (row) => (
      <Box variant="small" color="text-body-secondary">
        {row.errorMessage ? (
          <span title={row.errorMessage} style={clamp3}>
            {row.errorMessage}
          </span>
        ) : (
          <span style={anywhere}>
            {row.messageVariants > 1
              ? plural(row.messageVariants, "message variant")
              : "—"}
          </span>
        )}
      </Box>
    ),
  },
  {
    id: "items",
    header: COLUMN_LABELS.items,
    sortingField: "items",
    width: 120,
    cell: (row) => (
      <Box textAlign="right">
        <span style={nowrap}>{row.items.toLocaleString()}</span>
      </Box>
    ),
  },
  {
    id: "impact",
    header: COLUMN_LABELS.impact,
    sortingField: "impactUsd",
    width: 130,
    cell: (row) => (
      <Box
        textAlign="right"
        color={
          row.impactUsd !== null && row.impactUsd >= HIGH_IMPACT_USD
            ? "text-status-error"
            : undefined
        }
        fontWeight={
          row.impactUsd !== null && row.impactUsd >= HIGH_IMPACT_USD
            ? "bold"
            : undefined
        }
      >
        <span style={nowrap}>{formatImpact(row.impactUsd)}</span>
      </Box>
    ),
  },
  {
    id: "growth",
    header: COLUMN_LABELS.growth,
    sortingField: "growth",
    width: 130,
    cell: (row) => (
      <Box
        textAlign="right"
        color={
          row.growth !== null && row.growth > 0
            ? "text-status-error"
            : undefined
        }
      >
        <span style={nowrap}>{formatGrowth(row.growth)}</span>
      </Box>
    ),
  },
  {
    id: "lastSeen",
    header: COLUMN_LABELS.lastSeen,
    sortingField: "lastSeen",
    width: 170,
    cell: (row) => (
      <Box variant="small" color="text-body-secondary">
        <span style={nowrap}>{formatLastSeen(row.lastSeen)}</span>
      </Box>
    ),
  },
];

/** COLUMNS with the TrackStars chips carried in the Queue cell, for when Category is hidden. */
const COLUMNS_CATEGORY_PINNED: TableProps.ColumnDefinition<BacklogRow>[] =
  COLUMNS.map((col) =>
    col.id === "queue"
      ? {
          ...col,
          cell: (row) => (
            <div style={queueRow}>
              <Spanning
                value={row.queue}
                count={row.queues}
                noun="queue"
                maxWidth={QUEUE_MAX_WIDTH}
              />
              <TrackStarsChips patterns={row.group.trackstarsPatterns} />
            </div>
          ),
        }
      : col
  );

const PREVIEW_COLUMNS = ["queue", "errorType", "category", "items", "impact"];
/** The preview's page: the default (highest impact first) in the category view. */
const PREVIEW_QUERY: GroupsQuery = {
  ...DEFAULT_GROUPS_QUERY,
  byCategory: true,
};

/**
 * Header button for the selected row: one action for the table instead of one per row. A row
 * TrackStars identified as a pattern starts from that suggestion instead (same flow as its card
 * under Suggested by TrackStars), so the root cause and evidence come along; if the pattern is
 * already someone's case the action is to open it. Falls back to the plain start when the
 * pattern is no longer in the latest report or no host handles suggestions.
 */
const StartButton: React.FC<{
  stage: Stage;
  selected: BacklogRow | undefined;
  onStart: (seed: RowSeed) => void;
  onStartFromSuggestion?: (start: SuggestionStart) => void;
}> = ({ stage, selected, onStart, onStartFromSuggestion }) => {
  const patterns = selected?.group.trackstarsPatterns ?? [];
  const wantsSuggestion = !!onStartFromSuggestion && patterns.length > 0;
  const suggestions = useInvestigationSuggestions(stage, wantsSuggestion);
  const data = suggestions?.data;
  // Every suggestion this row may belong to that can still be started (not already a case).
  const options = wantsSuggestion
    ? (data?.suggestions ?? []).filter(
        (s) =>
          !!s.derivedRule?.length &&
          s.ownership.state !== "owned" &&
          patterns.includes(s.patternId)
      )
    : [];
  const start = (suggestion: InvestigationSuggestion) =>
    onStartFromSuggestion?.({
      suggestion,
      reportId: data?.report?.reportId,
      report: data?.report,
    });
  if (options.length > 1 && onStartFromSuggestion) {
    // Several distinct findings match this row: say so, and let the user pick one.
    return (
      <ButtonDropdown
        variant="primary"
        items={options.map((s) => ({
          id: s.patternId,
          text: `${shortPatternId(s.patternId)} · ${suggestionHeadline(s)}`,
          secondaryText: `${s.totalMatches.toLocaleString()} items`,
        }))}
        onItemClick={({ detail }) => {
          const picked = options.find((s) => s.patternId === detail.id);
          if (picked) start(picked);
        }}
      >
        {START_FROM_TRACKSTARS_LABEL} ({options.length} matches)
      </ButtonDropdown>
    );
  }
  if (options.length === 1 && onStartFromSuggestion) {
    const [suggestion] = options;
    return (
      <Button
        variant="primary"
        iconName="gen-ai"
        onClick={() => start(suggestion)}
      >
        {START_FROM_TRACKSTARS_LABEL} {shortPatternId(suggestion.patternId)}
      </Button>
    );
  }
  return (
    <Button
      variant="primary"
      disabled={!selected}
      loading={wantsSuggestion && suggestions?.isLoading}
      onClick={() => selected && onStart(seedFromGroup(selected.group))}
    >
      {START_LABEL}
    </Button>
  );
};

const SELECTION_LABELS = (
  total: number
): TableProps.AriaLabels<BacklogRow> => ({
  selectionGroupLabel: "Group selection",
  // Same shape as the default investigation title, category first: it is the human-assigned
  // attribute, so a screen reader hears the most meaningful part before the queue name.
  itemSelectionLabel: (_data, row) =>
    isCategoryRow(row)
      ? `${row.category} across ${plural(row.queues, "queue")}`
      : `${row.category}: ${row.errorType || "group"} on ${row.queue}`,
  allItemsSelectionLabel: () => `${total} groups`,
  expandButtonLabel: () => "Show queues",
  collapseButtonLabel: () => "Hide queues",
});

export interface MemberSort {
  field?: string;
  descending: boolean;
}

type SortValue = string | number | null;

const MEMBER_SORT_VALUE: Record<string, (row: BacklogRow) => SortValue> = {
  queue: (r) => r.queue || null,
  errorType: (r) => r.errorType || null,
  category: (r) => r.category || null,
  errorMessage: (r) => r.errorMessage || null,
  items: (r) => r.items,
  impactUsd: (r) => r.impactUsd,
  growth: (r) => r.growth,
  lastSeen: (r) => r.lastSeen,
};

/**
 * A category's queue rows in the table's sort order. The server sorts only the top-level rows
 * (members arrive by impact), so members are re-sorted here on the same column and direction.
 * Rows without a value sink to the bottom either way; ties keep the server's impact order.
 */
export function sortMembers(
  members: BacklogRow[],
  sort: MemberSort
): BacklogRow[] {
  const pick = sort.field ? MEMBER_SORT_VALUE[sort.field] : undefined;
  if (!pick) return members;
  const dir = sort.descending ? -1 : 1;
  return members
    .map((row, index) => ({ row, index, value: pick(row) }))
    .sort((a, b) => {
      if (a.value === null || b.value === null) {
        if (a.value === b.value) return a.index - b.index;
        return a.value === null ? 1 : -1;
      }
      const cmp =
        typeof a.value === "number" && typeof b.value === "number"
          ? a.value - b.value
          : String(a.value).localeCompare(String(b.value));
      return cmp === 0 ? a.index - b.index : cmp * dir;
    })
    .map((entry) => entry.row);
}

/**
 * Cloudscape expandable rows for the category view. Expansion is tracked by row key so a row stays
 * open across refetches (keepPreviousData hands back new objects for the same keys).
 */
function useExpandableRows(
  rows: BacklogRow[],
  sort?: MemberSort
): TableProps.ExpandableRows<BacklogRow> {
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());
  return {
    getItemChildren: (row) =>
      sort ? sortMembers(row.members ?? [], sort) : row.members ?? [],
    isItemExpandable: isCategoryRow,
    expandedItems: rows.filter((row) => expandedKeys.has(row.key)),
    onExpandableItemToggle: ({ detail }) =>
      setExpandedKeys((prev) => {
        const next = new Set(prev);
        if (detail.expanded) next.add(detail.item.key);
        else next.delete(detail.item.key);
        return next;
      }),
  };
}

// ---- Preview (work queue) ---------------------------------------------------------------------

interface BacklogPreviewProps {
  stage: Stage;
  onSeeAll: () => void;
  onStart: (seed: RowSeed) => void;
  onStartFromSuggestion?: (start: SuggestionStart) => void;
}

/**
 * The top few backlog groups by impact, shown on the work queue so the pre-grouped populations
 * are visible before anyone clicks anything. Uses the hook's default page (highest impact first).
 */
export const BacklogPreview: React.FC<BacklogPreviewProps> = ({
  stage,
  onSeeAll,
  onStart,
  onStartFromSuggestion,
}) => {
  const groups = useUninvestigatedGroups(stage, false, PREVIEW_QUERY);
  const [selected, setSelected] = useState<BacklogRow[]>([]);
  const { data } = groups;
  const rows = toBacklogRows(data?.groups || []);
  const top = rows.slice(0, PREVIEW_SIZE);
  const expandable = useExpandableRows(top);
  if (groups.error || (data && data.available === false)) return null;
  if (!groups.isLoading && !rows.length) return null;
  const total = data?.totalRows ?? data?.totalGroups ?? rows.length;
  return (
    <BacklogStage.Provider value={stage}>
      <Table
        columnDefinitions={COLUMNS}
        columnDisplay={Object.keys(COLUMN_LABELS).map((id) => ({
          id,
          visible: PREVIEW_COLUMNS.includes(id),
        }))}
        items={top}
        trackBy="key"
        expandableRows={expandable}
        selectionType="single"
        selectedItems={selected}
        onSelectionChange={({ detail }) => setSelected(detail.selectedItems)}
        ariaLabels={SELECTION_LABELS(top.length)}
        loading={groups.isLoading}
        loadingText="Loading groups"
        variant="container"
        wrapLines
        header={
          <Header
            variant="h3"
            description="Highest impact first. Select a group and start an investigation to take the whole population."
            actions={
              <StartButton
                stage={stage}
                selected={selected[0]}
                onStart={onStart}
                onStartFromSuggestion={onStartFromSuggestion}
              />
            }
          >
            Biggest groups nobody is on
          </Header>
        }
        footer={
          total > top.length ? (
            <Box textAlign="center">
              <Button variant="inline-link" onClick={onSeeAll}>
                {browseLabel(total)}
              </Button>
            </Box>
          ) : undefined
        }
      />
    </BacklogStage.Provider>
  );
};

// ---- Full view --------------------------------------------------------------------------------

const DEFAULT_PREFERENCES: CollectionPreferencesProps.Preferences = {
  pageSize: DEFAULT_PAGE_SIZE,
  wrapLines: true,
  stickyColumns: { first: 0, last: 0 },
  contentDisplay: Object.keys(COLUMN_LABELS).map((id) => ({
    id,
    visible: true,
  })),
};

const PREFERENCES_STORAGE_KEY = "lusca.investigations.backlog.preferences.v1";

/**
 * Saved table preferences, reconciled against the current columns: unknown ids are dropped
 * and columns added since the save are appended visible, so a new column never goes missing.
 */
export const loadBacklogPreferences =
  (): CollectionPreferencesProps.Preferences => {
    try {
      const raw = window.localStorage.getItem(PREFERENCES_STORAGE_KEY);
      if (!raw) return DEFAULT_PREFERENCES;
      const saved = JSON.parse(raw) as CollectionPreferencesProps.Preferences;
      const known = Object.keys(COLUMN_LABELS);
      const kept = (saved.contentDisplay ?? []).filter(
        (c) => known.includes(c.id) && typeof c.visible === "boolean"
      );
      const added = known
        .filter((id) => !kept.some((c) => c.id === id))
        .map((id) => ({ id, visible: true }));
      return {
        ...DEFAULT_PREFERENCES,
        ...saved,
        contentDisplay: [...kept, ...added],
      };
    } catch {
      return DEFAULT_PREFERENCES;
    }
  };

const saveBacklogPreferences = (
  prefs: CollectionPreferencesProps.Preferences
) => {
  try {
    window.localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // Storage full or disabled: the choice still applies for this session.
  }
};

/**
 * Resizable full table. Numeric columns get a fixed width; Category, Queue and Error type get
 * enough to show a typical value on one line; Message (which wraps) takes whatever is left, so
 * the table fills its width instead of handing the spare space to the last column. On a narrow
 * screen the one-line columns give up space (towards their minimums) before Message drops below
 * its own. A column the user drags keeps its saved width.
 */
const FIXED_COLUMN_WIDTHS: Record<string, number> = {
  // Wide enough for the header text plus its sort and filter icons on one line.
  items: 115,
  impact: 115,
  growth: 165,
  lastSeen: 150,
};
/** Target (fits ~40-character categories, ~45-character queues, ~30-character error types) and floor. */
const ONE_LINE_COLUMNS: Record<string, { target: number; min: number }> = {
  category: { target: 330, min: 180 },
  queue: { target: 310, min: 170 },
  errorType: { target: 200, min: 140 },
};
const MESSAGE_MIN_WIDTH = 200;
/**
 * Categorized slice: a category row's Queue, Error type and Message are almost always a count
 * ("2 queues", "20 message variants") or "—", so they stay narrow and Category (the row's
 * identity) takes the width.
 */
export const CATEGORIZED_NARROW_WIDTHS: Record<string, number> = {
  queue: 120,
  errorType: 135,
  errorMessage: 155,
};
const CATEGORIZED_CATEGORY_MAX_WIDTH = 520;
/** Fallback before the container is measured (and in tests). */
const FALLBACK_CONTAINER_WIDTH = 1400;
/** Selection radio, expand toggle and cell borders. */
const TABLE_CHROME_WIDTH = 72;

/** Widths for the visible columns: saved drags first, then fixed, then the text columns. */
export const fitColumnWidths = (
  visibleIds: string[],
  saved: Record<string, number>,
  containerWidth: number,
  categorized = false
): Record<string, number> => {
  const widths: Record<string, number> = {};
  let left = (containerWidth || FALLBACK_CONTAINER_WIDTH) - TABLE_CHROME_WIDTH;
  const oneLine: string[] = [];
  let message = false;
  visibleIds.forEach((id) => {
    const w = saved[id] ?? FIXED_COLUMN_WIDTHS[id];
    if (w !== undefined) {
      widths[id] = w;
      left -= w;
    } else if (ONE_LINE_COLUMNS[id]) oneLine.push(id);
    else if (id === "errorMessage") message = true;
  });
  if (categorized) {
    // Category rows mostly read "2 queues" / "—" in Queue, Error type and Message: those get a
    // small fixed width and Category takes the rest (spare beyond its cap goes to Message).
    oneLine.forEach((id) => {
      if (id === "category") return;
      widths[id] = CATEGORIZED_NARROW_WIDTHS[id];
      left -= widths[id];
    });
    if (message) {
      widths.errorMessage = CATEGORIZED_NARROW_WIDTHS.errorMessage;
      left -= widths.errorMessage;
    }
    if (oneLine.includes("category")) {
      widths.category = Math.max(
        ONE_LINE_COLUMNS.category.min,
        Math.min(CATEGORIZED_CATEGORY_MAX_WIDTH, Math.floor(left))
      );
      left -= widths.category;
    }
    if (left > 0) {
      const sink = message ? "errorMessage" : oneLine[0];
      if (sink) widths[sink] += Math.floor(left);
    }
    return widths;
  }
  const reserve = message ? MESSAGE_MIN_WIDTH : 0;
  const wanted = oneLine.reduce(
    (sum, id) => sum + ONE_LINE_COLUMNS[id].target,
    0
  );
  const slack = oneLine.reduce(
    (sum, id) => sum + ONE_LINE_COLUMNS[id].target - ONE_LINE_COLUMNS[id].min,
    0
  );
  // How far below target the one-line columns must go (0 = all at target, 1 = all at minimum).
  const squeeze =
    slack > 0 ? Math.min(1, Math.max(0, (wanted + reserve - left) / slack)) : 0;
  oneLine.forEach((id) => {
    const { target, min } = ONE_LINE_COLUMNS[id];
    widths[id] = Math.round(target - (target - min) * squeeze);
    left -= widths[id];
  });
  if (message)
    widths.errorMessage = Math.max(MESSAGE_MIN_WIDTH, Math.floor(left));
  else if (oneLine.length && left > 0) {
    // No Message column: spread the spare space over the one-line columns instead.
    const extra = Math.floor(left / oneLine.length);
    oneLine.forEach((id) => {
      widths[id] += extra;
    });
  }
  return widths;
};

const COLUMN_WIDTHS_STORAGE_KEY =
  "lusca.investigations.backlog.columnWidths.v6";

/** Widths the user dragged, by column id; anything unreadable is dropped. */
export const loadBacklogColumnWidths = (): Record<string, number> => {
  try {
    const raw = window.localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
    const saved = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const widths: Record<string, number> = {};
    Object.keys(COLUMN_LABELS).forEach((id) => {
      const w = saved[id];
      if (typeof w === "number" && Number.isFinite(w) && w > 0) widths[id] = w;
    });
    return widths;
  } catch {
    return {};
  }
};

/** Width of an element, tracked with a ResizeObserver (0 until measured or where unsupported). */
const useElementWidth = (): [(el: HTMLDivElement | null) => void, number] => {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) =>
      setWidth(Math.round(entry.contentRect.width))
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [el]);
  return [setEl, width];
};

const saveBacklogColumnWidths = (widths: Record<string, number>) => {
  try {
    window.localStorage.setItem(
      COLUMN_WIDTHS_STORAGE_KEY,
      JSON.stringify(widths)
    );
  } catch {
    // Storage full or disabled: the widths still apply for this session.
  }
};

interface InvestigationBacklogProps {
  stage: Stage;
  /** Shareable standalone URL for this view (shown when hosted in the dashboard drawer). */
  fullPageHref?: string;
  /**
   * Filter to apply, from the page URL or a sidebar preset. The table adopts it whenever it
   * changes (and resets to page 1); edits in the filter bar are reported through onFilterChange
   * so the host can keep the URL in step.
   */
  filter?: GroupsFilterQuery;
  onFilterChange?: (filter: GroupsFilterQuery | undefined) => void;
  onStart: (seed: RowSeed) => void;
  /** Start from a TrackStars suggestion when the selected row is one of its patterns. */
  onStartFromSuggestion?: (start: SuggestionStart) => void;
}

/**
 * The full backlog: a filterable, sortable, server-paged table of every reconciled group.
 * Selection is per page — a group is a single "Start investigation" target, not a batch.
 */
export const InvestigationBacklog: React.FC<InvestigationBacklogProps> = ({
  stage,
  fullPageHref,
  filter: externalFilter,
  onFilterChange: onExternalFilterChange,
  onStart,
  onStartFromSuggestion,
}) => {
  const [preferences, setPreferences] =
    useState<CollectionPreferencesProps.Preferences>(loadBacklogPreferences);
  const [columnWidths, setColumnWidths] = useState(loadBacklogColumnWidths);
  const [tableRef, tableWidth] = useElementWidth();
  const pageSize = Math.min(
    preferences.pageSize ?? DEFAULT_PAGE_SIZE,
    MAX_PAGE_SIZE
  );
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [filterQuery, setFilterQuery] = useState<PropertyFilterProps.Query>(
    () => fromGroupsFilter(externalFilter)
  );
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<BacklogRow>
  >(() => sortColumnFor(externalFilter));
  const [sortingDescending, setSortingDescending] = useState(true);
  // Adopt a new external filter (sidebar preset, back/forward) without echoing our own edits
  // back: compare wire forms, since the host may hand back the object we just reported. The
  // slice's natural order comes with it (growth for the growing slice, impact otherwise).
  const externalKey = serializeGroupsFilter(externalFilter) ?? "";
  const currentKey = useMemo(
    () => serializeGroupsFilter(toGroupsFilter(filterQuery)) ?? "",
    [filterQuery]
  );
  useEffect(() => {
    if (externalKey !== currentKey) {
      setFilterQuery(fromGroupsFilter(externalFilter));
      setSortingColumn(sortColumnFor(externalFilter));
      setSortingDescending(true);
      setCurrentPageIndex(1);
    }
    // Only react to the external filter changing, not to our own edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalKey]);
  const [selected, setSelected] = useState<BacklogRow[]>([]);

  const sort: GroupsSortField =
    (sortingColumn.sortingField &&
      SORT_FIELD_MAP[sortingColumn.sortingField]) ||
    "impactUsd";

  const groups = useUninvestigatedGroups(stage, false, {
    offset: (currentPageIndex - 1) * pageSize,
    limit: pageSize,
    filter: toGroupsFilter(filterQuery),
    sort,
    desc: sortingDescending,
    byCategory: true,
  });

  const { data } = groups;
  const rows = toBacklogRows(data?.groups || []);
  const expandable = useExpandableRows(rows, {
    field: sortingColumn.sortingField,
    descending: sortingDescending,
  });
  // Cloudscape reserves an expand-toggle gutter in the first column whenever expandableRows is
  // set, even if no row can expand. A page with no category rows (e.g. Uncategorized, whose
  // groups are flat) drops it, so Queue sits next to the selection radio.
  const anyExpandable = rows.some(isCategoryRow);
  const totalCount = data?.totalCount ?? 0;
  const totalRows = data?.totalRows ?? data?.totalGroups ?? totalCount;
  const pagesCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const filtered = filterQuery.tokens.length > 0;
  // Uncategorized is the one category spread over many rows, so only it hides the column
  // (TrackStars chips move to the Queue cell). Mixed or named-category tables keep it.
  const pinned = pinnedCategory(filterQuery) === UNCATEGORIZED;
  const columnDisplay = (preferences.contentDisplay ?? []).map((c) =>
    pinned && c.id === "category" ? { ...c, visible: false } : c
  );

  // If the total shrinks (filter/page-size change) below the current page, snap back.
  useEffect(() => {
    if (data && currentPageIndex > pagesCount) {
      setCurrentPageIndex(pagesCount);
    }
  }, [data, currentPageIndex, pagesCount]);

  // PropertyFilter only fires once a token is committed, so every change goes straight to the
  // server (no debounce) and always restarts from page 1.
  const onFilterChange = (query: PropertyFilterProps.Query) => {
    setFilterQuery(query);
    setCurrentPageIndex(1);
    onExternalFilterChange?.(toGroupsFilter(query));
  };
  const clearFilter = () => onFilterChange(EMPTY_FILTER);

  const slice = categorySliceOf(filterQuery);
  const sliceToken = filterQuery.tokens.find(isSliceToken);
  // What the bar shows: the query minus the token the switch stands for.
  const barQuery: PropertyFilterProps.Query =
    slice === "all"
      ? filterQuery
      : {
          ...filterQuery,
          tokens: filterQuery.tokens.filter((t) => t !== sliceToken),
        };
  const onBarChange = (query: PropertyFilterProps.Query) =>
    onFilterChange(
      slice === "all" || !sliceToken
        ? query
        : { ...query, tokens: [...query.tokens, sliceToken] }
    );
  // Picking a slice replaces any category token: the switch and a hand-typed category would fight.
  const setSlice = (next: CategorySlice) =>
    onFilterChange({
      operation: "and",
      tokens: [
        ...filterQuery.tokens.filter((t) => t.propertyKey !== "category"),
        ...(next === "all"
          ? []
          : [
              {
                propertyKey: "category",
                operator: next === "uncategorized" ? "=" : "!=",
                value: UNCATEGORIZED,
              },
            ]),
      ],
    });

  const onSortingChange: TableProps["onSortingChange"] = ({ detail }) => {
    setSortingColumn(detail.sortingColumn);
    setSortingDescending(!!detail.isDescending);
    setCurrentPageIndex(1);
  };

  const onPreferencesConfirm = (
    next: CollectionPreferencesProps.Preferences
  ) => {
    setPreferences(next);
    saveBacklogPreferences(next);
    if (next.pageSize !== preferences.pageSize) setCurrentPageIndex(1);
  };

  const counter = filtered
    ? `(${totalCount.toLocaleString()}/${totalRows.toLocaleString()})`
    : `(${totalRows.toLocaleString()})`;

  // One line, same shape as the Active table's totals: what's out there and what it's worth.
  const totalsLine = data?.available
    ? `${(filtered
        ? data.matchedItems ?? 0
        : data.totalUnassigned ?? 0
      ).toLocaleString()} ${
        filtered ? "matching items" : "items"
      } · ${formatImpact(
        filtered ? data.matchedImpactUsd : data.totalUnassignedImpactUsd
      )} est. impact`
    : undefined;
  // Freshness sits beside the header actions (Cloudscape "last updated"); the snapshot time is one
  // click away in a popover.
  const provenance = data?.available ? (
    <Box color="text-body-secondary" fontSize="body-s">
      <Popover
        triggerType="text"
        size="small"
        position="top"
        dismissButton={false}
        content={`DLQ snapshot ${formatSnapshotTimestamp(
          data.snapshotTimestamp
        )}`}
      >
        {data.reconciledAt
          ? `Updated ${formatWhen(new Date(data.reconciledAt))}`
          : "Not reconciled yet"}
      </Popover>
    </Box>
  ) : undefined;

  const header = (
    <Header
      variant="h3"
      counter={counter}
      description={totalsLine}
      actions={
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          {provenance}
          {fullPageHref && (
            <Button
              href={fullPageHref}
              target="_blank"
              iconAlign="right"
              iconName="external"
              variant="link"
            >
              Open as page
            </Button>
          )}
          <StartButton
            stage={stage}
            selected={selected[0]}
            onStart={onStart}
            onStartFromSuggestion={onStartFromSuggestion}
          />
        </SpaceBetween>
      }
    >
      {BACKLOG_TITLE}
    </Header>
  );

  if (groups.error) {
    return (
      <SpaceBetween size="m">
        <Alert type="error" header="Investigation service unavailable">
          {groups.error.message}
        </Alert>
      </SpaceBetween>
    );
  }
  if (data && data.available === false) {
    return (
      <Alert type="info" header="No reconciliation yet">
        The hourly reconciliation has not run for this stage. Groups appear
        after the first run.
      </Alert>
    );
  }

  const baseColumns = pinned ? COLUMNS_CATEGORY_PINNED : COLUMNS;
  const visibleIds = columnDisplay.filter((c) => c.visible).map((c) => c.id);
  // Cloudscape reads widths once per mount (a newly shown column takes its width prop), so
  // remount when the container width moves by a noticeable step (first measure, window resize).
  const widthStep = Math.round(tableWidth / 40);
  const fitted = fitColumnWidths(
    visibleIds,
    columnWidths,
    tableWidth,
    slice === "categorized"
  );
  const sizedColumns = baseColumns.map((col) => {
    const width = fitted[col.id ?? ""] ?? col.width;
    // Cloudscape never renders a column below its minWidth, so a narrower fitted width
    // (Categorized slice) lowers the floor too.
    const minWidth =
      typeof width === "number" && typeof col.minWidth === "number"
        ? Math.min(col.minWidth, width)
        : col.minWidth;
    return { ...col, width, minWidth };
  });
  const onColumnWidthsChange: TableProps["onColumnWidthsChange"] = ({
    detail,
  }) => {
    // detail.widths follows columnDefinitions order and reports every column's rendered width,
    // which drifts from the fitted width (last-column stretch, sub-pixel rounding). Save only the
    // one the user dragged, so the others keep re-fitting when the sidebar or window changes.
    let dragged: { id: string; width: number; delta: number } | undefined;
    detail.widths.forEach((w, i) => {
      const col = sizedColumns[i];
      if (!col?.id || !visibleIds.includes(col.id)) return;
      const delta = Math.abs(w - (col.width ?? w));
      if (delta > 1 && (!dragged || delta > dragged.delta))
        dragged = { id: col.id, width: w, delta };
    });
    if (!dragged) return;
    const next = { ...columnWidths, [dragged.id]: dragged.width };
    setColumnWidths(next);
    saveBacklogColumnWidths(next);
  };

  return (
    <BacklogStage.Provider value={stage}>
      <FillColumn.Provider value>
        <div ref={tableRef}>
          <Table
            key={`${pinned ? "pinned" : "all"}:${slice}:${widthStep}`}
            columnDefinitions={sizedColumns}
            items={rows}
            trackBy="key"
            expandableRows={anyExpandable ? expandable : undefined}
            selectionType="single"
            selectedItems={selected}
            onSelectionChange={({ detail }) =>
              setSelected(detail.selectedItems)
            }
            ariaLabels={SELECTION_LABELS(totalCount)}
            // Spinner only while the rows on screen are for another query (first load, page, filter,
            // sort). A background refetch of the same page (window focus: the hook refetches
            // "always") keeps the rows: blanking them on every tab switch looked like a page reload.
            loading={
              groups.isLoading || (groups.isFetching && !!groups.isPreviousData)
            }
            loadingText="Loading groups"
            sortingColumn={sortingColumn}
            sortingDescending={sortingDescending}
            onSortingChange={onSortingChange}
            columnDisplay={columnDisplay}
            stickyColumns={preferences.stickyColumns}
            // Drag a header edge to resize; widths persist per browser. Spare space
            // goes to the last visible column (Cloudscape's resizable layout).
            resizableColumns
            onColumnWidthsChange={onColumnWidthsChange}
            stickyHeader
            wrapLines={preferences.wrapLines}
            variant="container"
            header={header}
            empty={
              filtered ? (
                <Box textAlign="center" color="inherit">
                  <b>No matches</b>
                  <Box variant="p" color="inherit">
                    No groups match the current filters.
                  </Box>
                  <Button onClick={clearFilter}>Clear filters</Button>
                </Box>
              ) : (
                <Box textAlign="center" color="inherit">
                  <b>Nothing to investigate</b>
                  <Box variant="p" color="inherit">
                    Every DLQ item in the latest snapshot belongs to an
                    investigation.
                  </Box>
                </Box>
              )
            }
            filter={
              <PropertyFilter
                query={barQuery}
                onChange={({ detail }) => onBarChange(detail)}
                customControl={
                  <SegmentedControl
                    label="Category"
                    selectedId={slice}
                    onChange={({ detail }) =>
                      setSlice(detail.selectedId as CategorySlice)
                    }
                    options={[
                      { id: "all", text: "All" },
                      { id: "uncategorized", text: "Uncategorized" },
                      { id: "categorized", text: "Categorized" },
                    ]}
                  />
                }
                filteringProperties={BACKLOG_FILTERING_PROPERTIES}
                filteringOptions={backlogFilteringOptions(data?.filterOptions)}
                i18nStrings={PROPERTY_FILTER_I18N}
                countText={
                  barQuery.tokens.length > 0
                    ? `${totalCount.toLocaleString()} match${
                        totalCount === 1 ? "" : "es"
                      }`
                    : ""
                }
                expandToViewport
              />
            }
            pagination={
              <Pagination
                currentPageIndex={currentPageIndex}
                pagesCount={pagesCount}
                onChange={({ detail }) =>
                  setCurrentPageIndex(detail.currentPageIndex)
                }
                ariaLabels={{
                  nextPageLabel: "Next page",
                  previousPageLabel: "Previous page",
                  pageLabel: (n) => `Page ${n} of ${pagesCount}`,
                }}
              />
            }
            preferences={
              <CollectionPreferences
                title="Preferences"
                confirmLabel="Confirm"
                cancelLabel="Cancel"
                preferences={preferences}
                onConfirm={({ detail }) => onPreferencesConfirm(detail)}
                pageSizePreference={{
                  title: "Page size",
                  options: [
                    { value: 25, label: "25 groups" },
                    { value: 50, label: "50 groups" },
                    { value: 100, label: "100 groups" },
                    { value: 200, label: "200 groups" },
                  ],
                }}
                wrapLinesPreference={{
                  label: "Wrap lines",
                  description: "Wrap long error messages",
                }}
                stickyColumnsPreference={{
                  firstColumns: {
                    title: "Stick first column(s)",
                    description:
                      "Keep the first columns visible while scrolling the table sideways.",
                    options: [
                      { label: "None", value: 0 },
                      { label: "First column", value: 1 },
                      { label: "First two columns", value: 2 },
                    ],
                  },
                }}
                contentDisplayPreference={{
                  title: "Columns",
                  description:
                    "Show, hide and reorder columns. Saved in this browser.",
                  options: Object.entries(COLUMN_LABELS).map(([id, label]) => ({
                    id,
                    label,
                    alwaysVisible: id === "category",
                  })),
                }}
              />
            }
          />
        </div>
      </FillColumn.Provider>
    </BacklogStage.Provider>
  );
};

export default InvestigationBacklog;
